import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ApiError, api, type RefreshProgress } from './api';

/**
 * Starts or follows a background Hologram + RMS refresh and polls its
 * progress. `dataRunId` is the run a page load reported as already going —
 * the server starts one itself when the snapshot is due — so the page shows
 * its progress without anyone pressing Refresh. When the run ends, `reload`
 * is called with the run's start time, which the server uses to make sure it
 * serves the new copy rather than one it still holds in memory.
 */
export function useSnapshotRefresh(dataRunId: string | null | undefined, reload: (since: number) => Promise<void>) {
  const [ownRun, setOwnRun] = useState<string | null>(null);
  const [finished, setFinished] = useState<string | null>(null);
  // A run reported by the page wins unless it is one this hook already saw end.
  const followed = dataRunId && dataRunId !== finished ? dataRunId : ownRun;

  const start = useMutation({
    mutationFn: api.startSnapshotRefresh,
    onSuccess: async (r) => {
      if (r.runId) setOwnRun(r.runId);
      else await reload(0);
    },
  });

  const progress = useQuery({
    queryKey: ['snapshot-refresh', followed],
    enabled: followed !== null,
    // Finishing is handled here rather than in an effect: this runs exactly
    // once per fetch, so the reload cannot fire twice for one run.
    queryFn: async (): Promise<RefreshProgress | null> => {
      const runId = followed ?? '';
      try {
        const p = await api.getSnapshotRefresh(runId);
        if (p.state !== 'running') {
          setFinished(p.runId);
          await reload(p.startedAt);
        }
        return p;
      } catch (err) {
        // Superseded by a newer run: its copy is already the latest.
        if (err instanceof ApiError && err.status === 404) {
          setFinished(runId);
          await reload(0);
          return null;
        }
        throw err;
      }
    },
    refetchInterval: (q) => (q.state.data && q.state.data.state !== 'running' ? false : 1_000),
    refetchOnWindowFocus: false,
    retry: 2,
  });

  const running = progress.data?.state === 'running' || start.isPending || (followed !== null && progress.isPending);
  return {
    start: () => start.mutate(),
    running,
    progress: progress.data ?? null,
    error: (start.error ?? progress.error) as Error | null,
  };
}
